// Feedback types — agent app submits to the shared api.feedback service.
export type FeedbackType = 'bug_report' | 'feature_request' | 'general_feedback' | 'error_report';
export type FeedbackStatus = 'submitted' | 'under_review' | 'planned' | 'in_progress' | 'completed' | 'wont_fix';
export type FeedbackPriority = 'critical' | 'high' | 'medium' | 'low';
export type AttachmentType = 'image' | 'video' | 'file' | 'screenshot' | 'recording' | 'audio';

export interface IFeedbackSubmission {
	id: number;
	submitterId: string;
	type: FeedbackType;
	title: string;
	description: string;
	status: FeedbackStatus;
	priority: FeedbackPriority;
	severity: string | null;
	pageUrl: string | null;
	browserInfo: string | null;
	viewportSize: string | null;
	userRole: string | null;
	consoleErrors: string | null;
	metadata: string | null;
	adminResponse: string | null;
	adminRespondedAt: string | null;
	resolvedAt: string | null;
	voteCount: number;
	createdAt: string;
	updatedAt: string;
	isDeleted: boolean;
}

export interface IFeedbackAttachment {
	id: number;
	feedbackSubmissionId: number | null;
	filename: string;
	originalFilename: string;
	mimeType: string;
	size: number;
	url: string;
	type: AttachmentType;
	thumbnailUrl: string | null;
	createdAt: string;
}

export interface ICreateFeedbackRequest {
	type: FeedbackType;
	title: string;
	description: string;
	priority?: FeedbackPriority;
	pageUrl?: string;
	browserInfo?: string;
	viewportSize?: string;
	consoleErrors?: string[];
	metadata?: Record<string, unknown>;
	attachmentIds?: number[];
}

// UI labels
export const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
	bug_report: 'Report a Bug',
	feature_request: 'Request a Feature',
	general_feedback: 'General Feedback',
	error_report: 'Report an Error',
};

export const FEEDBACK_TYPE_DESCRIPTIONS: Record<FeedbackType, string> = {
	bug_report: 'Something in the app is broken or not working',
	feature_request: 'Suggest a new capability or improvement',
	general_feedback: 'Share comments, ideas or praise',
	error_report: 'You hit an error message or crash',
};

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
	submitted: 'Submitted',
	under_review: 'Under Review',
	planned: 'Planned',
	in_progress: 'In Progress',
	completed: 'Completed',
	wont_fix: "Won't Fix",
};

export const FEEDBACK_PRIORITY_LABELS: Record<FeedbackPriority, string> = {
	critical: 'Critical',
	high: 'High',
	medium: 'Medium',
	low: 'Low',
};

// Colors map to existing design tokens (resolved at runtime via CSS vars).
export const FEEDBACK_TYPE_COLORS: Record<FeedbackType, string> = {
	bug_report: 'var(--error-500)',
	feature_request: 'var(--accent-500)',
	general_feedback: 'var(--primary-500)',
	error_report: 'var(--warning-500)',
};

export const FEEDBACK_STATUS_COLORS: Record<FeedbackStatus, string> = {
	submitted: 'var(--neutral-500)',
	under_review: 'var(--primary-500)',
	planned: 'var(--accent-600)',
	in_progress: 'var(--warning-500)',
	completed: 'var(--success-500)',
	wont_fix: 'var(--error-500)',
};
